import { useDelayedFlag } from "@/common/hooks/useDelayedFlag";
import { type HatoColumnKey, useUpdateUiPreferences } from "@/features/accounts/api/accountsApi";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { useMemo, useState } from "react";
import { useAnimalsList } from "../api/animalsApi";
import { AnimalsList } from "../components/AnimalsList";
import { HatoEmptyState } from "../components/HatoEmptyState";
import type { SubStateFilterValue } from "../components/HatoFilters";
import { HatoListHeader } from "../components/HatoListHeader";
import { resolveHatoColumns } from "../hatoColumns";
import { lifecycleLabel } from "../lifecycleStates";
import { producingSubState } from "../producingSubState";
import type { LifecycleState } from "../types";
import { normalizeText } from "../utils/normalizeText";

export function HatoListPage() {
  const { currentAccount, currentMembership, isLoading: accountLoading } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const { data, isLoading: animalsLoading, isError, refetch } = useAnimalsList(accountId);
  const [search, setSearch] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleState | null>(null);
  const [lotFilter, setLotFilter] = useState<string | null>(null);
  const [subStateFilter, setSubStateFilter] = useState<SubStateFilterValue | null>(null);

  // columnas-hato: la visibilidad vive en la membresía (ui_preferences, server)
  // y llega vía useCurrentAccount — el update optimista del cache ["me"] hace
  // que el toggle se refleje al instante sin estado local duplicado.
  const columns = resolveHatoColumns(currentMembership?.uiPreferences);
  const updateUiPreferences = useUpdateUiPreferences(accountId, currentMembership?.id ?? "");
  const handleColumnToggle = (key: HatoColumnKey, visible: boolean) => {
    updateUiPreferences.mutate({ hatoColumns: { [key]: visible } });
  };

  // B83: el contexto de cuenta puede caer a null transitoriamente durante el
  // refetch de `useMe` (foco de ventana / staleTime) o un cambio de cuenta.
  // Mientras no haya cuenta resuelta o la lista no haya llegado, es "cargando":
  // NUNCA el empty-state sin data confirmada, para no parpadear "Tu hato está
  // vacío" sobre una cuenta que sí tiene animales.
  const accountReady = Boolean(currentAccount);
  const isLoading = accountLoading || !accountReady || animalsLoading;
  // Loader neutral, sin search bar ni empty CTA, para que la transición a empty
  // o populated no muestre layouts incompatibles. Delay de 200ms evita el flash
  // cuando la query resuelve antes que el ojo registre el spinner.
  const showLoader = useDelayedFlag(isLoading);

  // Filtros client-side: el endpoint devuelve toda la lista del hato. Para una
  // finca típica (<200 animales) esto es razonable. Si un cliente supera ese
  // umbral, mover filtros al backend con query params (ya hay índices en
  // animals.(account_id, lifecycle_state) y memberships.(lot_id, exited_at)).
  // URL params se deja fuera del MVP — filtros son exploratorios, persistirlos
  // agrega complejidad de parse/validate sin valor claro para este sprint.
  const filtered = useMemo(() => {
    const animals = data?.animals ?? [];
    // B84: normaliza el término del usuario y los campos buscados (diacríticos +
    // mayúsculas) para que "prenada" (sin ñ) matchee "Novilla preñada".
    const q = normalizeText(search.trim());
    return animals.filter((a) => {
      if (lifecycleFilter && a.lifecycleState !== lifecycleFilter) return false;
      if (lotFilter && a.currentLotId !== lotFilter) return false;
      if (subStateFilter) {
        const sub = producingSubState(a);
        if (sub !== subStateFilter) return false;
      }
      if (q) {
        const tagMatch = normalizeText(a.tag).includes(q);
        const nameMatch = a.name ? normalizeText(a.name).includes(q) : false;
        // D-5 oportunista §16.7: extender búsqueda al label del estado
        // ("vaca" matchea "Vaca en producción"). Usa el label consciente del
        // sexo (B85) — el mismo que el usuario ve en la fila.
        const stateMatch = normalizeText(lifecycleLabel(a.lifecycleState, a.sex)).includes(q);
        if (!tagMatch && !nameMatch && !stateMatch) return false;
      }
      return true;
    });
  }, [data, search, lifecycleFilter, lotFilter, subStateFilter]);

  const loaderFallback = (
    <>
      <title>Hato · HATO</title>
      {showLoader ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-app-border border-t-app-emerald" />
        </div>
      ) : null}
    </>
  );

  if (isLoading) {
    return loaderFallback;
  }

  if (isError) {
    return (
      <>
        <title>Hato · HATO</title>
        <HatoListHeader count={null} search={search} onSearch={setSearch} />
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-app-ink-2 text-sm">No se pudo cargar el hato.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
          >
            Reintentar
          </button>
        </div>
      </>
    );
  }

  const animals = data?.animals;

  // B83: el empty-state SOLO con data confirmada. Si llegamos sin data y sin
  // loading/error (ventana transitoria del refetch de cuenta), mostramos el
  // loader en vez de parpadear "Tu hato está vacío".
  if (!animals) {
    return loaderFallback;
  }

  if (animals.length === 0) {
    return (
      <>
        <title>Hato · HATO</title>
        <HatoEmptyState />
      </>
    );
  }

  const total = animals.length;

  const hasActiveFilter = Boolean(search.trim() || lifecycleFilter || lotFilter || subStateFilter);

  return (
    <>
      <title>Hato · HATO</title>
      <HatoListHeader
        count={total}
        search={search}
        onSearch={setSearch}
        accountId={accountId}
        lifecycleFilter={lifecycleFilter}
        onLifecycleFilterChange={setLifecycleFilter}
        lotFilter={lotFilter}
        onLotFilterChange={setLotFilter}
        subStateFilter={subStateFilter}
        onSubStateFilterChange={setSubStateFilter}
        columns={columns}
        onColumnToggle={handleColumnToggle}
      />
      {filtered.length === 0 ? (
        <p className="mt-8 text-center text-app-ink-3 text-sm">
          {hasActiveFilter
            ? "Sin resultados con los filtros actuales."
            : "Sin animales para mostrar."}
        </p>
      ) : (
        <div className="mt-4">
          <AnimalsList animals={filtered} columns={columns} />
        </div>
      )}
    </>
  );
}
